import { BaseQueryApi } from '@reduxjs/toolkit/dist/query';
import axios from 'axios';
import qs from 'qs';
import { ADMIN_ENDPOINT, ADMIN_ENDPOINT_SHORT, ENDPOINT } from 'src/constants/api';
import { downloadBlob } from 'src/utils/downloadBlob';

import { baseApi, setHeaders } from './base';

const refreshAccountFn = async (accountId: number, api: BaseQueryApi) => {
  try {
    const url = `${ADMIN_ENDPOINT_SHORT}/vehicles/all/refresh`;
    // @ts-ignore
    const headers = setHeaders(new axios.AxiosHeaders(), api);
    const result = await axios.post(
      url,
      { accountId },
      {
        //@ts-ignore
        headers,
      },
    );
    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError;
    return {
      error: {
        //@ts-ignore
        status: err.response?.status,
        //@ts-ignore
        data: err.response?.data || err.message,
      },
    };
  }
};

const accountApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCountries: builder.query<{ data: Country[] }, {}>({
      query: (userId: number) => {
        const params = { user: userId, active: true };
        return `${ADMIN_ENDPOINT}/countries?${qs.stringify(params)}`;
      },
    }),
    getAccountDetails: builder.query<{ data: Account }, {}>({
      query: (accountId: number) => {
        return `${ADMIN_ENDPOINT}/account/${accountId}`;
      },
    }),
    getIntegrations: builder.query<{ data: Integration[] }, void>({
      query: () => {
        return `${ENDPOINT}/admin/integrations/available`;
      },
    }),
    createIntegration: builder.mutation({
      query: ({
        accountId,
        serviceId,
        configuration,
      }: {
        accountId: number;
        serviceId: number;
        configuration: string;
      }) => ({
        url: `${ENDPOINT}/admin/account/${accountId}/integrations`,
        method: 'POST',
        body: {
          service_id: serviceId,
          configuration,
        },
      }),
    }),
    updateIntegration: builder.mutation({
      query: ({
        accountId,
        integrationId,
        serviceId,
        configuration,
      }: {
        accountId: number;
        integrationId: number;
        serviceId: number;
        configuration: string;
      }) => ({
        url: `${ENDPOINT}/admin/account/${accountId}/integrations/${integrationId}`,
        method: 'PUT',
        body: {
          service_id: serviceId,
          configuration,
        },
      }),
    }),
    deleteIntegration: builder.mutation({
      query: ({ accountId, integrationId }: { accountId: number; integrationId: number }) => ({
        url: `${ENDPOINT}/admin/account/${accountId}/integrations/${integrationId}`,
        method: 'DELETE',
      }),
    }),
    updateAccount: builder.mutation({
      query: ({ accountId, account }: { accountId: number; account: UpdateAccountRequest }) => ({
        url: `${ADMIN_ENDPOINT}/account/${accountId}/partial`,
        method: 'PATCH',
        body: account,
      }),
    }),
    getAccountUsers: builder.mutation({
      query: ({
        page,
        account,
        includeSelf,
      }: {
        page: number;
        account?: number;
        includeSelf?: boolean;
      }) => {
        const params: any = { page, per_page: 100, account };
        if (includeSelf) {
          params.include_self = includeSelf;
        }
        return `${ENDPOINT}/users?${qs.stringify(params)}`;
      },
    }),
    addAccountUser: builder.mutation({
      query: (user: User) => ({
        url: `${ENDPOINT}/users`,
        method: 'POST',
        body: user,
      }),
    }),
    addAdminUser: builder.mutation({
      query: (user: User) => ({
        url: `${ADMIN_ENDPOINT}/users`,
        method: 'POST',
        body: { ...user, countries: [179] },
      }),
    }),
    deleteAccountUser: builder.mutation({
      query: (userId: number) => ({
        url: `${ENDPOINT}/users/${userId}`,
        method: 'DELETE',
      }),
    }),
    deleteAdminUser: builder.mutation({
      query: (userId: number) => ({
        url: `${ADMIN_ENDPOINT}/users/${userId}`,
        method: 'DELETE',
      }),
    }),
    createAccount: builder.mutation({
      query: (values: object) => ({
        url: `${ADMIN_ENDPOINT}/account`,
        method: 'POST',
        body: values,
      }),
    }),
    getUsers: builder.mutation({
      query: ({ page, roles }: { page: number; roles?: number[] }) => {
        const params = { page, per_page: 100, roles };
        return `${ADMIN_ENDPOINT_SHORT}/users?${qs.stringify(params)}`;
      },
    }),
    refreshAccount: builder.mutation({
      queryFn: refreshAccountFn,
    }),
    disableAccount: builder.mutation({
      query: (accountId: number) => ({
        url: `${ADMIN_ENDPOINT}/account/${accountId}`,
        method: 'DELETE',
      }),
    }),
    enableAccount: builder.mutation({
      query: (accountId: number) => ({
        url: `${ADMIN_ENDPOINT_SHORT}/account/undelete/${accountId}`,
        method: 'POST',
      }),
    }),
    unexpireVehicle: builder.mutation({
      query: ({ accountId, vin }: { accountId: number; vin: string }) => {
        const data = new FormData();
        data.append('account', accountId.toString());
        data.append('vin', vin);

        return {
          url: `${ENDPOINT}/vehicles/unexpire`,
          body: data,
          method: 'POST',
        };
      },
    }),
    downloadImporterFile: builder.mutation<{ success: true; filename: string }, { url: string }>({
      query: ({ url }: { url: string }) => ({
        url: url.replace('http://', 'https://'),
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
          }

          const blob = await response.blob();
          const filename = `inventory-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;

          // Trigger download immediately - blob must not pass through Redux (gets corrupted)
          downloadBlob(blob, filename);

          return { success: true as const, filename };
        },
      }),
    }),
    getAccountPages: builder.mutation({
      query: () => {
        return `${ENDPOINT}/pages/users-pages`;
      },
    }),
    publishPage: builder.mutation({
      query: (pageId: number) => {
        return {
          url: `${ENDPOINT}/pages/${pageId}/publish`,
          method: 'PATCH',
        };
      },
    }),
    unpublishPage: builder.mutation({
      query: (pageId: number) => {
        return {
          url: `${ENDPOINT}/pages/${pageId}/unpublish`,
          method: 'PATCH',
        };
      },
    }),
    getUserAccounts: builder.query<{ data: Account[] }, number>({
      query: (userId: number) => {
        return `${ENDPOINT}/user/${userId}/accounts`;
      },
    }),
    updateUserAccounts: builder.mutation({
      query: ({ userId, accounts }: { userId: number; accounts: number[] }) => ({
        url: `${ENDPOINT}/user/${userId}/accounts`,
        method: 'PUT',
        body: { accounts },
      }),
    }),
  }),
});

export const {
  useLazyGetCountriesQuery,
  useLazyGetAccountDetailsQuery,
  useLazyGetIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useUpdateAccountMutation,
  useGetAccountUsersMutation,
  useAddAdminUserMutation,
  useAddAccountUserMutation,
  useDeleteAccountUserMutation,
  useDeleteAdminUserMutation,
  useCreateAccountMutation,
  useGetUsersMutation,
  useRefreshAccountMutation,
  useDisableAccountMutation,
  useEnableAccountMutation,
  useUnexpireVehicleMutation,
  useDownloadImporterFileMutation,
  useGetAccountPagesMutation,
  usePublishPageMutation,
  useUnpublishPageMutation,
  useLazyGetUserAccountsQuery,
  useUpdateUserAccountsMutation,
} = accountApi;
